<template>
  <div class="grid-block" :style="gridStyle">
    <div
      v-for="(card, idx) in block.cards"
      :key="idx"
      class="grid-card"
    >
      <q-icon
        v-if="card.icon"
        :name="card.icon"
        class="grid-card-icon"
        :style="{ color: card.accent || 'var(--color-ocean-light)' }"
      />
      <p class="grid-card-title">{{ card.title }}</p>
      <p class="grid-card-body">{{ card.body }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface GridCard {
  icon?: string;
  title: string;
  body: string;
  accent?: string;
}

interface GridBlock {
  type: 'grid';
  columns: 2 | 3 | 4;
  cards: GridCard[];
}

const props = defineProps<{ block: GridBlock }>();

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${props.block.columns}, 1fr)`,
}));
</script>

<style scoped lang="scss">
.grid-block {
  display: grid;
  gap: 16px;
  margin: 24px 0;

  /* On tablet: max 2 columns regardless of columns setting */
  @media (min-width: 640px) and (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  /* On mobile: always 1 column */
  @media (max-width: 639px) {
    grid-template-columns: 1fr !important;
  }
}

.grid-card {
  background: var(--color-sand-light);
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-lg);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.grid-card-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.grid-card-title {
  font-size: var(--oa-text-base);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
  line-height: 1.3;
}

.grid-card-body {
  font-size: var(--oa-text-sm);
  font-weight: 400;
  color: var(--oa-text-secondary);
  line-height: 1.6;
  margin: 0;
}
</style>
